import { useCallback, useEffect, useState } from "react";
import {
  Share2, Copy, Mail, Loader2, X, Eye, Ban, CalendarClock, Shield,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * ShareWithAttorney — generate a signed 1-90 day read-only URL that the
 * user can email to their attorney. Attorney needs no login.
 * Mounted on Case Workspace / Review section.
 */
export default function ShareWithAttorneyCard({ caseData }) {
  const caseId = caseData.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/shares`);
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (token) => {
     
    if (!window.confirm("Revoke this link? Anyone using it will lose access immediately.")) return;
    try {
      await api.post(`/cases/${caseId}/shares/${token}/revoke`);
      toast.success("Link revoked.");
      load();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const activeCount = rows.filter((r) => !r.revoked && !isExpired(r.expires_at)).length;

  return (
    <div className="card-surface p-6 mb-5" data-testid="share-attorney-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
          <Share2 className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Share with your attorney
          </h3>
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Generate a signed, read-only link (default 7 days) with your case timeline, notices,
            and AI drafts. Your attorney doesn&apos;t need to sign up.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => setOpen(true)}
          data-testid="create-share-btn"
        >
          <Share2 className="w-4 h-4 inline mr-1" /> Create share link
        </button>
      </div>

      {loading ? (
        <div className="text-xs flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : activeCount === 0 && rows.length === 0 ? (
        <div className="text-xs italic" style={{ color: "var(--text-secondary)" }}>
          No share links yet. Create one to hand your attorney a single URL that shows everything USCIS has sent so far.
        </div>
      ) : (
        <ul className="space-y-2 mt-2" data-testid="share-list">
          {rows.map((s) => (
            <ShareRow key={s.token} share={s} onRevoke={() => revoke(s.token)} />
          ))}
        </ul>
      )}

      {open && (
        <CreateShareModal
          onClose={() => setOpen(false)}
          onCreated={async (share) => {
            setOpen(false);
            await load();
            // Auto-open a small "your link is ready" modal right after
            showLinkReadyModal(share);
          }}
          caseId={caseId}
        />
      )}
    </div>
  );
}

function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

function daysLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function absoluteUrl(path) {
  return `${window.location.origin}${path}`;
}

function ShareRow({ share, onRevoke }) {
  const expired = isExpired(share.expires_at);
  const dl = daysLeft(share.expires_at);
  const status = share.revoked ? { label: "Revoked", pill: "pill-bad" }
    : expired ? { label: "Expired", pill: "pill-bad" }
    : { label: `${dl} day${dl === 1 ? "" : "s"} left`, pill: dl <= 2 ? "pill-warn" : "pill-ok" };
  const url = absoluteUrl(share.path);
  return (
    <li className="p-3 rounded-md flex items-center gap-3 flex-wrap"
        style={{ background: "#FFFFFF", border: "1px solid var(--border-stone)" }}
        data-testid={`share-row-${share.token}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`pill ${status.pill}`}>{status.label}</span>
          <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>
            <Eye className="w-3 h-3" /> {share.view_count} view{share.view_count === 1 ? "" : "s"}
          </span>
          {share.note && (
            <span className="text-[11px] italic truncate max-w-[240px]"
                  style={{ color: "var(--text-secondary)" }}>{share.note}</span>
          )}
        </div>
        <div className="text-[11px] font-mono truncate" style={{ color: "var(--text-secondary)" }}>{url}</div>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => copyText(url)}
                className="btn-ghost text-xs"
                data-testid={`share-copy-${share.token}`}>
          <Copy className="w-3.5 h-3.5 inline mr-1" /> Copy
        </button>
        {!share.revoked && !expired && (
          <a
            href={mailtoLink(url, share.note)}
            className="btn-ghost text-xs"
            data-testid={`share-email-${share.token}`}
          >
            <Mail className="w-3.5 h-3.5 inline mr-1" /> Email
          </a>
        )}
        {!share.revoked && !expired && (
          <button type="button" onClick={onRevoke}
                  className="btn-ghost text-xs" title="Revoke"
                  data-testid={`share-revoke-${share.token}`}>
            <Ban className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

function copyText(txt) {
  try {
    navigator.clipboard.writeText(txt);
    toast.success("Link copied to your clipboard.");
  } catch {
    toast.error("Couldn't copy. Select the URL and use Ctrl/Cmd+C.");
  }
}

function mailtoLink(url, note) {
  const subject = encodeURIComponent("My DS-260USA.com case — please review");
  const body = encodeURIComponent(
    `Hi,\n\nHere is a read-only view of my immigration case. It shows every USCIS notice, the timeline, and any AI-drafted response letters.\n\n${url}\n\n${note ? `Note: ${note}\n\n` : ""}This link is valid for a limited time and can be revoked at any moment.\n\nThank you!`,
  );
  return `mailto:?subject=${subject}&body=${body}`;
}

/* ---------- Create modal ---------- */

function CreateShareModal({ onClose, onCreated, caseId }) {
  const [ttl, setTtl] = useState(7);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/shares`, {
        ttl_days: Number(ttl),
        note: note.trim() || null,
        scope: "timeline_notices",
      });
      toast.success("Share link created — copy or email it below.");
      onCreated?.(data);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Create a share link" testId="create-share-modal">
      <div className="space-y-3">
        <div className="rounded-md p-3 text-xs flex items-start gap-2"
             style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)",
                      color: "var(--ai-text)" }}>
          <Shield className="w-3.5 h-3.5 mt-0.5" />
          <span>
            The link contains a random 256-bit token. Anyone with the URL can view your case
            (read-only) until it expires or you revoke it. Sensitive uploaded documents and
            signature images are never exposed.
          </span>
        </div>
        <div>
          <label className="field-label">Link duration</label>
          <select className="field-input" value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  data-testid="share-ttl-select">
            <option value={1}>1 day</option>
            <option value={3}>3 days</option>
            <option value={7}>7 days (recommended)</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days (max)</option>
          </select>
        </div>
        <div>
          <label className="field-label">Note for the attorney (optional)</label>
          <input className="field-input" value={note} maxLength={200}
                 placeholder="e.g. Please look at the RFE — response due Mar 5"
                 onChange={(e) => setNote(e.target.value)}
                 data-testid="share-note-input" />
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 justify-end">
        <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
        <button type="button" onClick={create} disabled={busy}
                className="btn-primary text-sm"
                data-testid="share-create-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : <Share2 className="w-4 h-4 inline mr-1" />}
          Create link
        </button>
      </div>
    </Modal>
  );
}

/* ---------- "Your link is ready" modal (post-create) ---------- */

let _modalRoot;
function showLinkReadyModal(share) {
  // Simple imperative modal via appending to body — dismissed on click outside
  const url = absoluteUrl(share.path);
  toast.custom((t) => (
    <div className="card-surface p-4 max-w-md" data-testid="share-link-ready-toast">
      <div className="font-display font-bold text-sm mb-2" style={{ color: "var(--text-primary)" }}>
        Your read-only link is ready 🎉
      </div>
      <div className="text-xs font-mono p-2 rounded-md break-all"
           style={{ background: "#F3F4F6", color: "var(--text-primary)" }}>
        {url}
      </div>
      <div className="mt-3 flex items-center gap-2 justify-end">
        <button className="btn-ghost text-xs" onClick={() => toast.dismiss(t)}>Close</button>
        <a className="btn-ghost text-xs" href={mailtoLink(url, share.note)}>
          <Mail className="w-3.5 h-3.5 inline mr-1" /> Email it
        </a>
        <button className="btn-primary text-xs" onClick={() => { copyText(url); toast.dismiss(t); }}>
          <Copy className="w-3.5 h-3.5 inline mr-1" /> Copy
        </button>
      </div>
    </div>
  ), { duration: 15000 });
  return _modalRoot;
}

function Modal({ onClose, title, testId, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(26,46,36,0.55)" }}
      onClick={onClose}
      data-testid={testId}
    >
      <div className="card-surface p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>{title}</h3>
          <button type="button" onClick={onClose} className="btn-ghost">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
